import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getChatComposerHotkey } from './chatComposerHotkeys.js';
import { clampChooserPage, resolveInitialChooserPage } from './composerChooserPaging.js';
import { CHOOSER_OTHER_INPUT_KEY, resolveSingleCustomInputChoiceKey, firstTruthy, firstDefined, choiceKey, chooserPageAnswer, focusElement, resolveChooserNavigationDirection, matchesHotkey, preventConfiguredDefault, canSubmitChooser, resolveChooserPageState, resolveChooserSelection } from "./chooserSelection.js";
import { actionInputValuesForPages, selectedOtherInputChoice, actionInputIsReady, resolveActionInputPageProgress, selectionKeysForFocusedChoice, resolveChooserSelectionUpdate, resolveChooserSubmissionProgression, consumeChooserPageProgression, submitChooserSelection, chooserIdentityFor, resolveChooserInputState, resolveChooserPrimaryAction } from "./chooserSubmission.js";
import { useChooserInitialization } from "./useChooserInitialization.js";
export * from "./chooserSelection.js";
export * from "./chooserSubmission.js";
export default function useComposerChooser({
  composerChooser,
  sendDisabled = false,
  onComposerActionSelect,
  onComposerChooserSubmit,
  onComposerChooserDismiss,
}) {
  const chooser = Object.assign({}, composerChooser);
  const chooserInteractive = chooser.presentation?.interactive !== false;
  const isBusy = sendDisabled || !chooserInteractive;
  const chooserFooter = Object.assign({}, chooser.footer);
  const chooserRef = useRef(null);
  const chooserInlineInputRef = useRef(null);
  const initializedChooserIdentityRef = useRef('');
  const [chooserPage, setChooserPage] = useState(1);
  const [chooserSelectedKey, setChooserSelectedKey] = useState('');
  const [chooserCustomInput, setChooserCustomInput] = useState('');
  const [chooserSelections, setChooserSelections] = useState({});
  const [chooserCustomInputs, setChooserCustomInputs] = useState({});

  const chooserPages = useMemo(
    () => (Array.isArray(chooser.pages) ? chooser.pages : []),
    [chooser.pages],
  );
  const chooserIdentity = chooserIdentityFor(chooser);
  const initialChooserPage = resolveInitialChooserPage(composerChooser, chooserPages.length);
  const hasComposerChooser = chooserPages.length > 0;
  const normalizedChooserPage = useMemo(() => {
    if (!hasComposerChooser) {
      return 1;
    }
    return clampChooserPage(chooserPage, chooserPages.length);
  }, [chooserPage, chooserPages.length, hasComposerChooser]);

  const chooserPageState = resolveChooserPageState({
    chooser, chooserPages, hasComposerChooser, normalizedChooserPage, chooserSelections,
  });
  const {
    chooserOptions,
    chooserTotalPages,
    currentChooserPage,
    currentPageKey,
    currentSelectionMode,
    selectedKeys,
  } = chooserPageState;
  const {
    chooserAllowsOtherInput,
    chooserOtherInputSelected,
    chooserConfirmOnSelect,
    chooserHideSubmit,
  } = resolveChooserInputState({ currentChooserPage, selectedKeys, chooser });
  const chooserOptionsKey = useMemo(
    () => chooserOptions.map((item, index) => String(choiceKey(item) || `choice-${index}`)).join('\u001f'),
    [chooserOptions],
  );
  const singleCustomInputChoiceKey = resolveSingleCustomInputChoiceKey(currentChooserPage);
  const chooserSelection = resolveChooserSelection({
    chooser, chooserOptions, selectedKeys, chooserSelectedKey, singleCustomInputChoiceKey,
  });
  const {
    allowsImplicitOptionFallback,
    chooserRecommendedKey,
    effectiveChooserSelectedKey,
    fallbackOptionKey,
    submissionFallbackChoice,
  } = chooserSelection;
  const effectiveChooserCustomInput = String(firstDefined(
    chooserCustomInputs[currentPageKey],
    chooserCustomInput,
  ));

  useChooserInitialization({chooser, hasComposerChooser, initialChooserPage, chooserIdentity, chooserOptionsKey, chooserRecommendedKey, fallbackOptionKey, singleCustomInputChoiceKey, allowsImplicitOptionFallback, chooserPages, normalizedChooserPage, initializedChooserIdentityRef, chooserRef, setChooserPage, setChooserSelectedKey, setChooserCustomInput, setChooserSelections, setChooserCustomInputs});
  const chooserSubmitHotkey = getChatComposerHotkey('chooser_submit');
  const chooserDismissHotkey = getChatComposerHotkey('chooser_dismiss');

  const moveChooserSelection = useCallback((direction) => {
    if (isBusy) return;
    if (![hasComposerChooser, chooserOptions.length > 0].every(Boolean)) {
      return;
    }
    const currentIndex = chooserOptions.findIndex((item) => choiceKey(item) === effectiveChooserSelectedKey);
    const fallbackIndex = chooserOptions.findIndex((item) => choiceKey(item) === chooserRecommendedKey);
    const startIndex = currentIndex >= 0 ? currentIndex : (fallbackIndex >= 0 ? fallbackIndex : 0);
    const nextIndex = (startIndex + direction + chooserOptions.length) % chooserOptions.length;
    const nextKey = choiceKey(chooserOptions[nextIndex]);
    setChooserSelections((current) => ({ ...current, [currentPageKey]: [nextKey] }));
    setChooserSelectedKey(nextKey);
  }, [chooserOptions, chooserRecommendedKey, currentPageKey, effectiveChooserSelectedKey, hasComposerChooser, isBusy]);

  const handleChooserDismiss = useCallback(() => {
    if (!chooserInteractive || chooser.dismissible === false) return;
    if (typeof onComposerChooserDismiss === 'function') {
      onComposerChooserDismiss();
    }
  }, [chooser.dismissible, chooserInteractive, onComposerChooserDismiss]);

  const actionInputValues = useMemo(
    () => actionInputValuesForPages({ chooserPages, chooserSelections, chooserCustomInputs }),
    [chooserCustomInputs, chooserPages, chooserSelections],
  );
  const actionInputReady = actionInputIsReady(chooser, actionInputValues);
  const actionInputPageProgress = resolveActionInputPageProgress({
    chooserKind: chooser.kind,
    actionInputValues,
    normalizedChooserPage,
    chooserPages,
  });

  const handleChooserSubmit = useCallback((explicitChoice = null) => {
    if (isBusy) return;
    const isTextPage = currentChooserPage?.inputKind === 'free_text';
    if (![hasComposerChooser, [isTextPage, chooserOptions.length > 0].some(Boolean)].every(Boolean)) {
      return;
    }
    const selectedChoice = firstTruthy(
      explicitChoice,
      chooserOptions.find((item) => choiceKey(item) === effectiveChooserSelectedKey),
      chooserOptions.find((item) => choiceKey(item) === chooserRecommendedKey),
      submissionFallbackChoice,
      selectedOtherInputChoice(currentChooserPage, selectedKeys),
    ) ?? null;
    if (![selectedChoice, isTextPage].some(Boolean)) {
      return;
    }
    const selectedChoiceRecord = Object.assign({}, selectedChoice);
    const answerValue = chooserPageAnswer(currentChooserPage, selectedChoiceRecord, effectiveChooserCustomInput);
    if (!answerValue) {
      return;
    }
    const progression = resolveChooserSubmissionProgression({
      chooserKind: chooser.kind,
      pageProgress: actionInputPageProgress,
      actionInputReady,
    });
    if (consumeChooserPageProgression({
      progression,
      setChooserPage,
      pageCount: chooserPages.length,
    })) {
      return;
    }

    submitChooserSelection({
      onComposerChooserSubmit,
      onComposerActionSelect,
      composerChooser,
      selectedChoice,
      selectedChoiceRecord,
      chooserSelectedKey,
      chooserRecommendedKey,
      chooserCustomInput: effectiveChooserCustomInput,
      actionInputValues,
      answerValue,
    });
  }, [
    isBusy,
    actionInputReady,
    actionInputPageProgress,
    actionInputValues,
    chooser.kind,
    chooserOptions,
    chooserPages.length,
    chooserRecommendedKey,
    chooserSelectedKey,
    currentChooserPage,
    effectiveChooserSelectedKey,
    effectiveChooserCustomInput,
    hasComposerChooser,
    composerChooser,
    onComposerActionSelect,
    onComposerChooserSubmit,
    submissionFallbackChoice,
    selectedKeys,
  ]);

  const handleChooserKeyDown = useCallback((event) => {
    if (!hasComposerChooser || isBusy) {
      return;
    }
    const navigationDirection = resolveChooserNavigationDirection(event);
    if (navigationDirection !== 0) {
      event.preventDefault();
      moveChooserSelection(navigationDirection);
      return;
    }
    if (matchesHotkey(chooserSubmitHotkey, event)) {
      preventConfiguredDefault(chooserSubmitHotkey, event);
      handleChooserSubmit();
      return;
    }
    if (matchesHotkey(chooserDismissHotkey, event)) {
      preventConfiguredDefault(chooserDismissHotkey, event);
      handleChooserDismiss();
    }
  }, [
    chooserDismissHotkey,
    chooserSubmitHotkey,
    handleChooserDismiss,
    handleChooserSubmit,
    hasComposerChooser,
    isBusy,
    moveChooserSelection,
  ]);

  const selectedChooserOption = chooserOptions.find(
    (item) => choiceKey(item) === effectiveChooserSelectedKey
  ) ?? null;
  const selectedOption = Object.assign({}, selectedChooserOption);
  const requiresCustomInput = [
    selectedOption.allowCustomInput,
    selectedOption.requiresCustomInput,
    chooserOtherInputSelected,
    currentChooserPage?.inputKind === 'free_text',
  ].some(Boolean);
  const primaryAction = resolveChooserPrimaryAction({
    chooserKind: chooser.kind,
    pageProgress: actionInputPageProgress,
    actionInputReady,
    chooserFooter,
  });
  const chooserCanSubmit = [!isBusy, primaryAction.pageActionReady, canSubmitChooser(
    requiresCustomInput,
    effectiveChooserCustomInput,
    effectiveChooserSelectedKey
  )].every(Boolean);
  const chooserPrimaryActionLabel = primaryAction.label;

  useEffect(() => {
    if ([hasComposerChooser, chooserInteractive, requiresCustomInput].every(Boolean)) {
      focusElement(chooserInlineInputRef);
    }
  }, [chooserSelectedKey, chooserInteractive, hasComposerChooser, requiresCustomInput]);

  const setCurrentChooserSelectedKeys = useCallback((keys) => {
    const normalized = Array.isArray(keys) ? keys.map(String) : [String(keys || '')].filter(Boolean);
    const selectionUpdate = resolveChooserSelectionUpdate({
      selectionMode: currentSelectionMode,
      currentKeys: selectedKeys,
      nextKeys: normalized,
    });
    if (selectionUpdate.shouldAdvance) {
      handleChooserSubmit();
      return;
    }
    setChooserSelections((current) => ({
      ...current,
      [currentPageKey]: selectionUpdate.keys,
    }));
    setChooserSelectedKey(selectionUpdate.keys[0] || '');
  }, [currentPageKey, currentSelectionMode, handleChooserSubmit, selectedKeys]);

  const setCurrentChooserSelectedKey = useCallback((key) => {
    const normalizedKey = String(key || '').trim();
    setChooserSelectedKey(normalizedKey);
    setChooserSelections((current) => ({
      ...current,
      [currentPageKey]: selectionKeysForFocusedChoice({
        selectionMode: currentSelectionMode,
        selectedKeys: current[currentPageKey],
        focusedKey: normalizedKey,
      }),
    }));
  }, [currentPageKey, currentSelectionMode]);

  const setCurrentChooserCustomInput = useCallback((value) => {
    setChooserCustomInputs((current) => ({ ...current, [currentPageKey]: value }));
    setChooserCustomInput(value);
  }, [currentPageKey]);

  const setCurrentChooserOtherInputSelected = useCallback((selected) => {
    setChooserSelections((current) => {
      const currentKeys = Array.isArray(current[currentPageKey]) ? current[currentPageKey] : [];
      const retainedKeys = currentKeys.filter((key) => key !== CHOOSER_OTHER_INPUT_KEY);
      const nextKeys = selected === true
        ? currentSelectionMode === 'multiple'
          ? [...retainedKeys, CHOOSER_OTHER_INPUT_KEY]
          : [CHOOSER_OTHER_INPUT_KEY]
        : retainedKeys;
      return { ...current, [currentPageKey]: nextKeys };
    });
    setChooserSelectedKey(selected === true ? CHOOSER_OTHER_INPUT_KEY : '');
  }, [currentPageKey, currentSelectionMode]);

  return {
    chooserAllowsOtherInput,
    chooserCanSubmit,
    chooserConfirmOnSelect,
    chooserCustomInput: effectiveChooserCustomInput,
    chooserDismissHotkey,
    chooserHideSubmit,
    chooserInlineInputRef,
    chooserOptions,
    chooserOtherInputSelected,
    chooserPages,
    chooserPrimaryActionLabel,
    chooserRecommendedKey,
    chooserRef,
    chooserSelectedKey: effectiveChooserSelectedKey,
    chooserSelectedKeys: selectedKeys,
    chooserSelectionMode: currentSelectionMode,
    chooserSubmitHotkey,
    chooserTotalPages,
    currentChooserPage,
    handleChooserDismiss,
    handleChooserKeyDown,
    handleChooserSubmit,
    hasComposerChooser,
    normalizedChooserPage,
    setChooserCustomInput: setCurrentChooserCustomInput,
    setChooserOtherInputSelected: setCurrentChooserOtherInputSelected,
    setChooserPage,
    setChooserSelectedKey: setCurrentChooserSelectedKey,
    setChooserSelectedKeys: setCurrentChooserSelectedKeys,
  };
}
